import React, { useState, useCallback, useEffect } from "react";
import { modernStyles } from "../FormStyles";
import FieldDescriptionCallout from "./FieldDescriptionCallout";
import {
  getUserByEmail,
  searchStaffByPBSPeoplePicker,
} from "../../../Service/UserService";

interface PeoplePickerProps {
  label: any;
  id?: any;
  required?: boolean;
  multi?: boolean;
  value?:
    | string
    | number
    | boolean
    | Date
    | string[]
    | { results: string[] }
    | null;
  onChange: (value: string | { results: string[] } | null) => void;
  onPersonaSelected?: (persona: any) => void;
  placeholder?: string;
  description?: string;
  className?: string;
  hideLabel?: boolean;
}

// Inline styles for the people picker container and suggestions
const peoplePickerContainerStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "620px",
  boxSizing: "border-box",
  overflow: "visible",
  position: "relative",
};

const inputShellStyle: React.CSSProperties = {
  width: "100%",
  minHeight: modernStyles.fieldHeight,
  borderRadius: modernStyles.borderRadius,
  border: "1px solid hsl(var(--border))",
  fontSize: "14px",
  padding: "4px 8px",
  boxSizing: "border-box",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 4,
  backgroundColor: "hsl(var(--input))",
  color: "hsl(var(--foreground))",
};

const chipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 6px",
  borderRadius: 999,
  background: "hsl(var(--primary))",
  fontSize: 12,
  color: "#FFF",
};

const chipRemoveStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 12,
  color: "#FFF",
};

const suggestionsDropdownStyle: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 10,
  background: "hsl(var(--background))",
  borderRadius: 8,
  boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
  marginTop: 4,
  maxHeight: 260,
  overflowY: "auto",
};

const suggestionItemStyle: React.CSSProperties = {
  padding: "6px 10px",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  fontSize: 13,
};

/**
 * PeoplePicker Component using Fluent UI v8 NormalPeoplePicker
 * Features: User search via API, validation, Outlook format support, most recently used
 * Styling: Matches form theme with consistent borders, hover/focus states, and responsive design
 */
const PeoplePicker: React.FC<PeoplePickerProps> = ({
  label,
  id,
  required = false,
  multi = false,
  value,
  onChange,
  onPersonaSelected,
  placeholder = "Search for people...",
  description,
  className,
  hideLabel = false,
}) => {
  const [query, setQuery] = useState("");
  const [mostRecentlyUsed, setMostRecentlyUsed] = useState<any[]>([]);
  const [selectedPersonas, setSelectedPersonas] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Parse initial selected personas from value prop - fetch full user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      const personas: any[] = [];

      if (value) {
        const selectedIds: string[] = [];

        if (typeof value === "object" && "results" in value) {
          selectedIds.push(...value.results);
        } else if (Array.isArray(value)) {
          selectedIds.push(...value);
        } else {
          selectedIds.push(String(value));
        }

        // Fetch user info for each ID
        for (const id of selectedIds) {
          // First check if we have cached user data in mostRecentlyUsed
          const cachedUser = mostRecentlyUsed.find(
            (u) => String(u.key) === String(id),
          );
          if (cachedUser) {
            personas.push(cachedUser);
            continue;
          }

          try {
            const response = await getUserByEmail(id);
            const user = response?.data.value[0];
            personas.push(
              normalizePersona({
                key: user.SPID,
                name: user.FullName,
                text: user.FullName,
                email: user.Email,
                secondaryText: [user.Role?.trim(), user.Region?.trim()]
                  .filter(Boolean)
                  .join(", "),
                tertiaryText: user.FullName,
                imageUrl: "",
                imageInitials: (user.FullName || "")
                  .split(" ")
                  .map((n: string) => n[0])
                  .join("")
                  .toUpperCase(),
                ValidationState: "valid",
              }),
            );
          } catch (error) {
            console.error(`Error fetching user info for ID ${id}:`, error);
            // Fallback to ID-only persona if fetch fails
            personas.push({
              key: id,
              name: `User ${id}`,
              text: `User ${id}`,
              imageUrl: "",
              imageInitials: "",
            });
          }
        }
      }

      setSelectedPersonas(personas);
    };

    fetchUserInfo();
  }, [value, mostRecentlyUsed]);

  // Handle picker suggestions (search results)
  const onFilterChanged = useCallback(
    async (filterText: string, currentPersonas?: any[]): Promise<any[]> => {
      if (!filterText || filterText.length < 2) {
        return [];
      }

      try {
        setIsLoading(true);
        // Transform input to handle Outlook format
        const transformedQuery = transformInputForOutlook(filterText);

        // Search API

        const response = await searchStaffByPBSPeoplePicker(transformedQuery);

        const searchResults = response.data.value || response.data || [];
        let personas: any[] = searchResults.map((user: any) =>
          normalizePersona({
            key: user.SPID,
            name: user.FullName,
            text: user.FullName,
            email: user.Email,
            secondaryText: [user.Role?.trim(), user.Region?.trim()]
              .filter(Boolean)
              .join(", "),
            tertiaryText: user.Email,
            imageUrl: "",
            imageInitials: (user.FullName || "")
              .split(" ")
              .map((n: string) => n[0])
              .join("")
              .toUpperCase(),
          }),
        );

        // Remove already selected
        if (currentPersonas && currentPersonas.length) {
          personas = removeDuplicates(personas, currentPersonas);
        }

        return personas;
      } catch (error: any) {
        console.error("Error searching users:", error);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // Handle persona selection
  const handleSelectionChange = useCallback(
    (items?: any[]) => {
      if (!items) return;

      // Normalize selected items to ensure they have all required properties
      const normalizedItems = items.map((item) => normalizePersona(item));
      setSelectedPersonas(normalizedItems);

      // Track in most recently used
      normalizedItems.forEach((item) => {
        if (item.key) {
          setMostRecentlyUsed((prev) => {
            const filtered = prev.filter((u) => u.key !== item.key);
            return [item, ...filtered].slice(0, 5); // Keep last 5
          });
        }
      });

      // Update parent
      if (multi) {
        onChange({ results: normalizedItems.map((p) => String(p.key)) });
      } else {
        onChange(
          normalizedItems.length > 0 ? String(normalizedItems[0].key) : null,
        );
        // For single-select, also call onPersonaSelected with full persona data for field mapping
        if (normalizedItems.length > 0 && onPersonaSelected) {
          onPersonaSelected(normalizedItems[0]);
        }
      }
    },
    [multi, onChange, onPersonaSelected],
  );

  return (
    <div style={peoplePickerContainerStyle} className={className}>
      {!hideLabel && (
        <label
          className="people-picker-label"
          htmlFor={id}
          style={{
            fontSize: 14,
            fontWeight: "bold",
            color: "hsl(var(--foreground))",
            marginBottom: 6,
            display: "block",
            padding: "5px 0px",
          }}
        >
          {label}
          {required && <span style={{ color: "hsl(var(--error))" }}> *</span>}
          {description && <FieldDescriptionCallout description={description} />}
        </label>
      )}
      <div
        className="people-picker-wrapper"
        style={{
          display: "flex",
          alignItems: "stretch",
          color: "hsl(var(--foreground))",
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        <div style={inputShellStyle} onClick={() => setOpen(true)} tabIndex={0}>
          {selectedPersonas.map((p) => (
            <span key={p.key} style={chipStyle}>
              <span>{p.text || p.name}</span>
              <button
                type="button"
                style={chipRemoveStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  const remaining = selectedPersonas.filter(
                    (sp) => sp.key !== p.key,
                  );
                  handleSelectionChange(remaining);
                }}
              >
                ×
              </button>
            </span>
          ))}
          {(!multi || selectedPersonas.length === 0 || multi) && (
            <input
              id={id}
              type="text"
              value={query}
              onChange={async (e) => {
                const val = onInputChange(e.target.value);
                setQuery(val);
                if (val.length >= 2) {
                  const results = await onFilterChanged(val, selectedPersonas);
                  setSuggestions(results);
                  setOpen(true);
                } else {
                  setSuggestions([]);
                }
              }}
              placeholder={placeholder}
              style={{
                flex: 1,
                minWidth: 80,
                border: "none",
                outline: "none",
                fontSize: 14,
                color: "hsl(var(--foreground))",
                backgroundColor: "transparent",
              }}
            />
          )}
        </div>
        {open && (suggestions.length > 0 || isLoading) && (
          <div style={suggestionsDropdownStyle}>
            {isLoading && (
              <div style={{ padding: 8, fontSize: 12 }}>Searching...</div>
            )}
            {!isLoading && suggestions.length === 0 && (
              <div style={{ padding: 8, fontSize: 12 }}>No results found</div>
            )}
            {suggestions.map((s) => (
              <div
                key={s.key}
                style={suggestionItemStyle}
                onClick={() => {
                  const nextItems = multi ? [...selectedPersonas, s] : [s];
                  handleSelectionChange(nextItems);
                  setQuery("");
                  setSuggestions([]);
                  setOpen(false);
                }}
              >
                <span style={{ fontWeight: 600 }}>{s.text || s.name}</span>
                {(s.secondaryText || s.tertiaryText) && (
                  <span
                    style={{
                      fontSize: 12,
                      color: "hsl(var(--foreground) / 0.8)",
                    }}
                  >
                    {s.secondaryText || s.tertiaryText}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PeoplePicker;

/**
 * Helper Functions
 */

/**
 * Transforms input to handle Outlook format
 * e.g., "Aaron Reid <aaron@example.com>" → "aaron@example.com"
 */
function transformInputForOutlook(input: string): string {
  const outlookRegEx = /<.*>/g;
  const emailAddress = outlookRegEx.exec(input);

  if (emailAddress && emailAddress[0]) {
    return emailAddress[0].substring(1, emailAddress[0].length - 1);
  }

  return input;
}

/**
 * Removes duplicate personas
 */
function removeDuplicates(personas: any[], existingPersonas?: any[]): any[] {
  if (!existingPersonas || !existingPersonas.length) {
    return personas;
  }
  return personas.filter(
    (persona) =>
      !existingPersonas.some((existing) => existing.key === persona.key),
  );
}

/**
 * Transform input (Outlook format handling)
 */
function onInputChange(input: string): string {
  return transformInputForOutlook(input);
}

/**
 * Normalize persona object to ensure all required properties exist
 */
function normalizePersona(persona: any): any {
  return {
    key: persona.key || "",
    name: persona.name || "",
    text: persona.text || persona.name || "",
    email: persona.email || "",
    secondaryText: persona.secondaryText || "",
    tertiaryText: persona.tertiaryText || "",
    imageUrl: persona.imageUrl || "",
    imageInitials: persona.imageInitials || "",
    ValidationState: persona.ValidationState || "valid",
  };
}
